import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, FilterCard, Field, SimpleSelect, EmptyState } from "@/components/common";
import SiportFormFields from "@/components/SiportFormFields";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  FileText, Search, Eye, Pencil, Trash2, FileSpreadsheet, Loader2, ChevronLeft, ChevronRight,
  ArrowUpDown, ShieldCheck, RotateCcw, Siren, AlertTriangle,
} from "lucide-react";
import {
  EwsDot, fmtTanggal, fmtWaktu, alasanCodeBlueText, reportToForm, toPayload, validateForm, downloadBlob,
} from "@/lib/siport";

const ALL = "__all__";
const EMPTY_FILTER = {
  search: "", tanggal_dari: "", tanggal_sampai: "", poliklinik: ALL, ews: ALL, tindak_lanjut: ALL,
  code_blue: ALL, status: ALL, poli_manual: ALL,
};
const FIELD_NAMA = {
  tanggal_kejadian: "Tanggal Input Form", poliklinik_nama: "Poliklinik", nama_dokter_pengisi: "Nama Pengisi Form",
  nama_pasien: "Nama Pasien", usia: "Usia", no_rm: "No. RM", diagnosa: "Diagnosa", penyakit_dasar: "Penyakit Dasar",
  jenis_percepatan: "Jenis Percepatan", alasan_percepatan: "Alasan Percepatan", ews_score: "Score EWS",
  kriteria_masalah: "Kriteria Masalah", tindak_lanjut: "Tindak Lanjut", status_verifikasi: "Status Verifikasi",
  "code_blue.alasan": "Alasan Code Blue", "code_blue.alasan_lainnya": "Alasan Lainnya",
  "code_blue.jam_aktivasi": "Jam Aktivasi", "code_blue.jam_tmrc_datang": "Jam TMRC Datang",
  "code_blue.respon_time_menit": "Respon Time", "code_blue.akhir_code_blue": "Akhir Code Blue",
  poliklinik_manual: "Poli Isian Manual",
};
const AKSI_NAMA = { edit: "edit", verifikasi: "verifikasi", jadikan_poli_tetap: "poli dijadikan pilihan tetap", gabung_poli: "poli digabungkan" };

const auditVal = (v) => (typeof v === "boolean" ? (v ? "Ya" : "Tidak") : String(v ?? "—"));

export function ManualBadge() {
  return (
    <Badge variant="outline" className="ml-1.5 px-1.5 py-0 text-[10px] font-semibold border-amber-300 text-amber-800 bg-amber-50"
      title="Nama poliklinik diisi manual (pilihan Lainnya)">Manual</Badge>
  );
}

export function StatusBadge({ status }) {
  return status === "Terverifikasi" ? (
    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"><ShieldCheck className="w-3 h-3 mr-1" />Terverifikasi</Badge>
  ) : (
    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Baru</Badge>
  );
}

export function EwsBadge({ ews }) {
  if (!ews) return "—";
  return (
    <span className="inline-flex items-center gap-1.5 text-sm">
      <EwsDot warna={ews.warna} />
      <span>{ews.label.split(" (")[0]}</span>
    </span>
  );
}

function buildParams(filter, extra = {}) {
  const p = new URLSearchParams();
  Object.entries(filter).forEach(([k, v]) => {
    if (v && v !== ALL) p.set(k, typeof v === "string" ? v.trim() : v);
  });
  Object.entries(extra).forEach(([k, v]) => p.set(k, v));
  return p.toString();
}

function DetailRow({ label, children }) {
  return (
    <div className="grid grid-cols-3 gap-3 py-2 border-b border-slate-100 last:border-0 text-sm">
      <div className="text-slate-500">{label}</div>
      <div className="col-span-2 text-slate-900 whitespace-pre-wrap break-words">{children ?? "—"}</div>
    </div>
  );
}

export default function SiportData() {
  const { isSuperAdmin, isSiportViewer, isKepalaUnit } = useAuth();
  const readOnly = isSiportViewer;
  const [meta, setMeta] = useState({ options: {}, poliklinik: [] });
  const [poliNames, setPoliNames] = useState([]);
  const [filter, setFilter] = useState(EMPTY_FILTER);
  const [applied, setApplied] = useState(EMPTY_FILTER);
  const [sort, setSort] = useState({ field: "tanggal_kejadian", order: "desc" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page_size: 20 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [delConfirm, setDelConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [detail, setDetail] = useState(null);
  const [edit, setEdit] = useState(null); // {id, form}
  const [editErrors, setEditErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/siport/form-options").then((r) => setMeta(r.data));
    api.get("/siport/poliklinik").then((r) => setPoliNames(r.data));
  }, []);

  const load = () => {
    setLoading(true);
    api.get(`/siport/reports?${buildParams(applied, { sort: sort.field, order: sort.order, page, page_size: 20 })}`)
      .then((r) => setData(r.data))
      .catch((err) => toast.error(apiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [applied, sort, page]);

  const apply = (e) => {
    e?.preventDefault();
    setPage(1);
    setApplied({ ...filter });
  };
  const resetFilter = () => {
    setFilter(EMPTY_FILTER);
    setApplied(EMPTY_FILTER);
    setPage(1);
  };
  const toggleSort = (field) =>
    setSort((s) => ({ field, order: s.field === field && s.order === "desc" ? "asc" : "desc" }));

  const openDetail = (id) => api.get(`/siport/reports/${id}`).then((r) => setDetail(r.data));
  const openEdit = (r) => {
    setEditErrors({});
    api.get(`/siport/reports/${r.id}`).then((res) => setEdit({ id: r.id, nomor: res.data.nomor_laporan, form: reportToForm(res.data) }));
  };

  const saveEdit = async () => {
    const errs = validateForm(edit.form, meta.options, meta.hari_ini);
    setEditErrors(errs);
    if (Object.keys(errs).length) return toast.error("Periksa isian yang belum lengkap");
    setSaving(true);
    try {
      const res = await api.put(`/siport/reports/${edit.id}`, toPayload(edit.form, meta.options));
      toast.success(res.data.message);
      setEdit(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/siport/reports/${id}/verifikasi`, { status_verifikasi: status });
      toast.success(`Status: ${status}`);
      if (detail?.id === id) openDetail(id);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const del = async () => {
    if (!delTarget || delConfirm.trim() !== delTarget.nomor_laporan) return;
    setDeleting(true);
    try {
      await api.delete(`/siport/reports/${delTarget.id}`);
      toast.success(`Laporan ${delTarget.nomor_laporan} dihapus permanen`);
      setDelTarget(null);
      setDelConfirm("");
      setDetail(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Gagal menghapus laporan");
    } finally {
      setDeleting(false);
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/siport/export/excel?${buildParams(applied)}`, { responseType: "blob" });
      downloadBlob(res, "Rekap_SIPORT.xlsx");
      toast.success("Excel berhasil diunduh");
    } catch {
      toast.error("Gagal membuat file Excel");
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));
  const opt = (k) => [{ label: "Semua", value: ALL }, ...(meta.options[k] || []).map((o) => ({ label: o.label, value: o.id }))];
  const setF = (k) => (v) => setFilter((f) => ({ ...f, [k]: v }));
  const filterActive = useMemo(() => JSON.stringify(applied) !== JSON.stringify(EMPTY_FILTER), [applied]);

  const SortHead = ({ field, children, className = "" }) => (
    <TableHead className={className}>
      <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => toggleSort(field)}>
        {children}
        <ArrowUpDown className={`w-3 h-3 ${sort.field === field ? "text-primary" : "text-slate-300"}`} />
      </button>
    </TableHead>
  );

  return (
    <div data-testid="siport-data-page">
      <PageHeader icon={FileText} title="Data Laporan SIPORT"
        subtitle={readOnly ? "Semua laporan percepatan layanan & Code Blue dari form publik."
          : "Semua laporan percepatan layanan & Code Blue dari form publik. Tandai Terverifikasi setelah dicek."}>
        {!isKepalaUnit && (
          <Button onClick={exportExcel} disabled={exporting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="siport-export-excel">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
            Export Excel
          </Button>
        )}
      </PageHeader>

      <form onSubmit={apply}>
        <FilterCard>
          <Field label="Cari">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={filter.search} onChange={(e) => setF("search")(e.target.value)} placeholder="Nama / No. RM / No. laporan"
                className="pl-8 w-[230px] bg-white" data-testid="siport-search" />
            </div>
          </Field>
          <Field label="Tanggal Input dari">
            <Input type="date" value={filter.tanggal_dari} onChange={(e) => setF("tanggal_dari")(e.target.value)} className="w-[150px] bg-white" />
          </Field>
          <Field label="Tanggal Input sampai">
            <Input type="date" value={filter.tanggal_sampai} onChange={(e) => setF("tanggal_sampai")(e.target.value)} className="w-[150px] bg-white" />
          </Field>
          <Field label="Poliklinik">
            <SimpleSelect value={filter.poliklinik} onChange={setF("poliklinik")} className="w-[200px]"
              options={[{ label: "Semua", value: ALL }, ...poliNames.map((n) => ({ label: n, value: n }))]} testId="siport-filter-poli" />
          </Field>
          <Field label="Poli isian manual">
            <SimpleSelect value={filter.poli_manual} onChange={setF("poli_manual")} className="w-[130px]" testId="siport-filter-poli-manual"
              options={[{ label: "Semua", value: ALL }, { label: "Ya", value: "ya" }, { label: "Tidak", value: "tidak" }]} />
          </Field>
          <Field label="Score EWS">
            <SimpleSelect value={filter.ews} onChange={setF("ews")} options={opt("ews_score")} className="w-[190px]" testId="siport-filter-ews" />
          </Field>
          <Field label="Tindak Lanjut">
            <SimpleSelect value={filter.tindak_lanjut} onChange={setF("tindak_lanjut")} options={opt("tindak_lanjut")} className="w-[190px]" testId="siport-filter-tl" />
          </Field>
          <Field label="Code Blue">
            <SimpleSelect value={filter.code_blue} onChange={setF("code_blue")} className="w-[110px]" testId="siport-filter-cb"
              options={[{ label: "Semua", value: ALL }, { label: "Ya", value: "ya" }, { label: "Tidak", value: "tidak" }]} />
          </Field>
          <Field label="Status">
            <SimpleSelect value={filter.status} onChange={setF("status")} className="w-[150px]" testId="siport-filter-status"
              options={[{ label: "Semua", value: ALL }, { label: "Baru", value: "Baru" }, { label: "Terverifikasi", value: "Terverifikasi" }]} />
          </Field>
          <Button type="submit" data-testid="siport-apply-filter"><Search className="w-4 h-4 mr-2" />Terapkan</Button>
          {filterActive && (
            <Button type="button" variant="ghost" onClick={resetFilter}><RotateCcw className="w-4 h-4 mr-2" />Reset</Button>
          )}
        </FilterCard>
      </form>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3 text-sm text-slate-500">
            <span>{data.total} laporan{filterActive ? " (terfilter)" : ""}</span>
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          </div>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <SortHead field="nomor_laporan">No. Laporan</SortHead>
                  <SortHead field="tanggal_kejadian">Tanggal Input Form</SortHead>
                  <SortHead field="poliklinik_nama">Poliklinik</SortHead>
                  <SortHead field="nama_pasien">Pasien</SortHead>
                  <SortHead field="usia">Usia</SortHead>
                  <TableHead>EWS</TableHead>
                  <TableHead>Tindak Lanjut</TableHead>
                  <TableHead>Respon</TableHead>
                  <SortHead field="status_verifikasi">Status</SortHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((r) => (
                  <TableRow key={r.id} data-testid={`siport-row-${r.id}`}>
                    <TableCell className="font-mono text-xs whitespace-nowrap">
                      {r.nomor_laporan}
                      {r.perlu_dicek && (
                        <span title="Data import perlu dicek"><AlertTriangle className="inline w-3.5 h-3.5 ml-1 text-amber-500" /></span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{fmtTanggal(r.tanggal_kejadian)}</TableCell>
                    <TableCell className="max-w-[200px]">
                      <span className="truncate">{r.poliklinik_nama}</span>
                      {r.poliklinik_manual && <ManualBadge />}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{r.nama_pasien}</div>
                      <div className="text-xs text-slate-500 tabular-nums">{r.no_rm}</div>
                    </TableCell>
                    <TableCell className="tabular-nums">{r.usia ?? "—"}</TableCell>
                    <TableCell className="whitespace-nowrap"><EwsBadge ews={r.ews_score} /></TableCell>
                    <TableCell>
                      {r.is_code_blue ? (
                        <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100"><Siren className="w-3 h-3 mr-1" />Code Blue</Badge>
                      ) : (
                        <span className="text-sm">{r.tindak_lanjut?.label || "—"}</span>
                      )}
                    </TableCell>
                    <TableCell className="tabular-nums whitespace-nowrap">
                      {r.code_blue?.respon_time_menit != null ? `${r.code_blue.respon_time_menit} mnt` : "—"}
                    </TableCell>
                    <TableCell><StatusBadge status={r.status_verifikasi} /></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" onClick={() => openDetail(r.id)} data-testid={`siport-detail-${r.id}`}>
                        <Eye className="w-4 h-4 text-slate-500" />
                      </Button>
                      {!readOnly && (
                        <Button size="icon" variant="ghost" onClick={() => openEdit(r)} data-testid={`siport-edit-${r.id}`}>
                          <Pencil className="w-4 h-4 text-slate-500" />
                        </Button>
                      )}
                      {isSuperAdmin && (
                        <Button size="icon" variant="ghost" title="Hapus permanen" onClick={() => { setDelTarget(r); setDelConfirm(""); }}
                          data-testid={`siport-row-delete-${r.id}`}>
                          <Trash2 className="w-4 h-4 text-rose-600" />
                        </Button>
                      )}
                      {!readOnly && r.status_verifikasi !== "Terverifikasi" && (
                        <Button size="icon" variant="ghost" title="Tandai Terverifikasi" onClick={() => setStatus(r.id, "Terverifikasi")}
                          data-testid={`siport-verify-${r.id}`}>
                          <ShieldCheck className="w-4 h-4 text-emerald-600" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!loading && data.items.length === 0 && (
              <EmptyState icon={FileText} title="Belum ada laporan" desc="Laporan dari form publik akan tampil di sini." />
            )}
          </div>
          <div className="flex items-center justify-end gap-2 mt-4 text-sm">
            <span className="text-slate-500 mr-2">Halaman {page} dari {totalPages}</span>
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Konfirmasi hapus permanen (Super Admin) */}
      <Dialog open={!!delTarget} onOpenChange={(o) => { if (!o && !deleting) { setDelTarget(null); setDelConfirm(""); } }}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-md overflow-x-hidden" data-testid="siport-delete-dialog">
          {delTarget && (
            <>
              <DialogHeader>
                <DialogTitle className="text-rose-700">Hapus permanen laporan?</DialogTitle>
                <DialogDescription>
                  Penghapusan tidak bisa dibatalkan. Data laporan hilang selamanya dan nomornya tidak dipakai ulang.
                </DialogDescription>
              </DialogHeader>
              <div className="min-w-0 space-y-3">
                <dl className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm space-y-1 break-words">
                  <div className="flex gap-2"><dt className="w-24 shrink-0 text-slate-500">No. Laporan</dt><dd className="font-mono font-semibold min-w-0">{delTarget.nomor_laporan}</dd></div>
                  <div className="flex gap-2"><dt className="w-24 shrink-0 text-slate-500">Tgl Input</dt><dd className="min-w-0">{fmtTanggal(delTarget.tanggal_kejadian)}</dd></div>
                  <div className="flex gap-2"><dt className="w-24 shrink-0 text-slate-500">Poliklinik</dt><dd className="min-w-0">{delTarget.poliklinik_nama}</dd></div>
                  <div className="flex gap-2"><dt className="w-24 shrink-0 text-slate-500">Pasien</dt><dd className="min-w-0">{delTarget.nama_pasien}</dd></div>
                </dl>
                <div className="space-y-1 min-w-0">
                  <label className="text-sm text-slate-700" htmlFor="siport-delete-confirm">
                    Ketik <span className="font-mono font-semibold">{delTarget.nomor_laporan}</span> untuk mengonfirmasi
                  </label>
                  <Input id="siport-delete-confirm" value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)}
                    autoComplete="off" className="w-full max-w-full font-mono" placeholder={delTarget.nomor_laporan}
                    data-testid="siport-delete-confirm-input" />
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => { setDelTarget(null); setDelConfirm(""); }} disabled={deleting}>Batal</Button>
                <Button onClick={del} disabled={deleting || delConfirm.trim() !== delTarget.nomor_laporan}
                  className="bg-rose-600 hover:bg-rose-700" data-testid="siport-confirm-delete">
                  {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Hapus permanen
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="siport-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  {detail.nomor_laporan} <StatusBadge status={detail.status_verifikasi} />
                </DialogTitle>
                <DialogDescription>
                  Masuk {fmtWaktu(detail.created_at)} · sumber {detail.source === "import_excel" ? "import Excel" : "form publik"}
                </DialogDescription>
              </DialogHeader>
              {detail.catatan_import?.length > 0 && (
                <div className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-3">
                  <div className="font-semibold mb-1">Catatan import (perlu dicek):</div>
                  <ul className="list-disc pl-5">{detail.catatan_import.map((c, i) => <li key={i}>{c}</li>)}</ul>
                </div>
              )}
              <div>
                <DetailRow label="Tanggal Input Form">{fmtTanggal(detail.tanggal_kejadian)}</DetailRow>
                <DetailRow label="Poliklinik">{detail.poliklinik_nama}{detail.poliklinik_manual && <ManualBadge />}</DetailRow>
                <DetailRow label="Nama Pengisi Form">{detail.nama_dokter_pengisi}</DetailRow>
                <DetailRow label="Nama Pasien">{detail.nama_pasien}</DetailRow>
                <DetailRow label="Usia">{detail.usia != null ? `${detail.usia} tahun` : "—"}</DetailRow>
                <DetailRow label="No. RM">{detail.no_rm}</DetailRow>
                <DetailRow label="Diagnosa">{detail.diagnosa}</DetailRow>
                <DetailRow label="Penyakit Dasar">{detail.penyakit_dasar?.label}</DetailRow>
                <DetailRow label="Jenis Percepatan">{detail.jenis_percepatan?.label}</DetailRow>
                <DetailRow label="Alasan Percepatan">{detail.alasan_percepatan?.label}</DetailRow>
                <DetailRow label="Score EWS"><EwsBadge ews={detail.ews_score} /></DetailRow>
                <DetailRow label="Kriteria Masalah">{detail.kriteria_masalah?.label}</DetailRow>
                <DetailRow label="Tindak Lanjut">{detail.tindak_lanjut?.label}</DetailRow>
                {detail.code_blue && (
                  <>
                    <DetailRow label="Alasan Code Blue">{alasanCodeBlueText(detail.code_blue)}</DetailRow>
                    <DetailRow label="Jam Aktivasi">{detail.code_blue.jam_aktivasi}</DetailRow>
                    <DetailRow label="Jam TMRC Datang">{detail.code_blue.jam_tmrc_datang}</DetailRow>
                    <DetailRow label="Respon Time">
                      {detail.code_blue.respon_time_menit != null ? `${detail.code_blue.respon_time_menit} menit` : "—"}
                    </DetailRow>
                    <DetailRow label="Akhir Code Blue">{detail.code_blue.akhir_code_blue}</DetailRow>
                  </>
                )}
              </div>

              <div>
                <div className="text-sm font-semibold text-slate-700 mb-2">Riwayat Perubahan</div>
                {detail.audit_log?.length ? (
                  <ul className="space-y-2 text-sm" data-testid="siport-audit-log">
                    {[...detail.audit_log].reverse().map((a, i) => (
                      <li key={i} className="rounded-md border border-slate-200 p-2.5">
                        <div className="text-xs text-slate-500">
                          {fmtWaktu(a.waktu)} · <b className="text-slate-700">{a.nama || a.oleh}</b> (@{a.oleh}) · {AKSI_NAMA[a.aksi] || a.aksi}
                        </div>
                        <ul className="mt-1 text-slate-700">
                          {a.perubahan.map((c, j) => (
                            <li key={j}>
                              {FIELD_NAMA[c.field] || c.field}: <span className="line-through text-slate-400">{auditVal(c.dari)}</span> → {auditVal(c.ke)}
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-400">Belum ada perubahan.</p>
                )}
              </div>

              {!readOnly && <DialogFooter className="gap-2 sm:justify-between">
                <div>
                  {isSuperAdmin && (
                    <Button variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="siport-delete"
                      onClick={() => { setDelTarget(detail); setDelConfirm(""); }}>
                      <Trash2 className="w-4 h-4 mr-2" />Hapus Permanen
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  {detail.status_verifikasi === "Terverifikasi" ? (
                    <Button variant="outline" onClick={() => setStatus(detail.id, "Baru")}>Kembalikan ke Baru</Button>
                  ) : (
                    <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setStatus(detail.id, "Terverifikasi")}>
                      <ShieldCheck className="w-4 h-4 mr-2" />Tandai Terverifikasi
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => { const d = detail; setDetail(null); openEdit(d); }}>
                    <Pencil className="w-4 h-4 mr-2" />Edit
                  </Button>
                </div>
              </DialogFooter>}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit */}
      <Dialog open={!readOnly && !!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="siport-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Laporan {edit?.nomor}</DialogTitle>
            <DialogDescription>Setiap perubahan dicatat di riwayat (siapa & kapan).</DialogDescription>
          </DialogHeader>
          {edit && (
            <SiportFormFields form={edit.form} options={meta.options} poliklinik={meta.poliklinik}
              poliManual={meta.poliklinik_manual} errors={editErrors}
              hariIni={meta.hari_ini}
              setForm={(fn) => setEdit((e) => ({ ...e, form: typeof fn === "function" ? fn(e.form) : fn }))} />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Batal</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="siport-save-edit">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
